import { FC, useCallback, useEffect, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Col, Row, Spin } from 'antd';
import { Modal as antModal } from 'antd';
import { useForm } from 'antd/es/form/Form';
import SectionTitle from 'component/section-title/sectionTitle';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useParams } from 'react-router-dom';
import { MY_EVENTS_ROUTE } from 'routes/events/list';
import { Form, Icon, Label, Link, Modal, Switch, Typography, message } from 'ui';

import { activeEventNameSelector } from 'selectors/eventsSlice.selector';
import {
  useCreateExtraTermsMutation,
  useDeleteExtraTermsMutation,
  useEditExtraTermsMutation,
  useLazyGetExtraTermsQuery,
} from 'services/events/eventsApiService';
import { ExtraTermsResType, ExtraTermsType } from 'services/events/evetns.api.type';
import { useAppSelector } from 'store/index';

import EventLoadingState from 'containers/events-loading-state';

import s from './ExtraTC.module.scss';
import ExtraTCItem from './extra-tc-item';

const { confirm } = antModal;

const ExtraTC: FC = () => {
  const { eventId } = useParams();
  const [form] = useForm();
  const eventName = useAppSelector(activeEventNameSelector);
  const [html, setHtml] = useState<string>('');
  const [createExtraTC, { isLoading: creating }] = useCreateExtraTermsMutation();
  const [deleteExtraTC] = useDeleteExtraTermsMutation();
  const [editExtraTC, { isLoading: editing }] = useEditExtraTermsMutation();
  const [getExtraTC, { isFetching: fetching }] = useLazyGetExtraTermsQuery();

  const [editExtarTerm, setEditExtarTerm] = useState<ExtraTermsType | null>(null);
  const [extraTCData, setExtraTCData] = useState<ExtraTermsResType | null>();
  const [open, setOpen] = useState(false);

  const [plainText, setPlainText] = useState('');

  const handleQuillChange = (value: string) => {
    setHtml(value);

    // Convert HTML to plain text
    const parsedText = new DOMParser().parseFromString(value, 'text/html').body.textContent || '';

    setPlainText(parsedText);
  };

  const showModal = () => {
    setEditExtarTerm(null);
    setPlainText('');
    setOpen(true);
  };
  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    form.resetFields();
    setOpen(false);
  };

  useEffect(() => {
    const fetchExtraTerms = async () => {
      if (eventId) {
        try {
          const res = await getExtraTC(eventId);
          if ('data' in res) {
            setExtraTCData(res?.data);
          }
        } catch (error) {
          console.error('Error fetching extra T&C:', error);
        }
      }
    };

    fetchExtraTerms();
  }, [getExtraTC, eventId]);

  const showConfirm = async (id: string) => {
    confirm({
      title: 'Do you want to delete this extra T&C?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      centered: true,
      mask: false,
      async onOk() {
        const res = await deleteExtraTC(id);

        if ('data' in res) {
          message.success('Extra T&C removed successfully.');
          setExtraTCData(res?.data);
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleDeleteItem = (id: string) => {
    showConfirm(id);
  };

  const handleEditItem = (extraTC: ExtraTermsType) => {
    setEditExtarTerm(extraTC);
    setOpen(true);
  };

  useEffect(() => {
    if (form && editExtarTerm && open) {
      form.setFieldValue('htmlMessage', editExtarTerm?.htmlMessage);
      setPlainText(editExtarTerm?.message);
      form.setFieldValue('isRequired', editExtarTerm?.isRequired);
      form.setFieldValue('showOnTicket', editExtarTerm?.showOnTicket);
      form.setFieldValue('disabled', editExtarTerm?.disabled);
    }
  }, [editExtarTerm, open, form]);

  const handleSubmit = useCallback(
    async (data) => {
      if (data && eventId) {
        if (!editExtarTerm) {
          const result = {
            eventId: eventId,
            htmlMessage: data.htmlMessage,
            message: plainText,
            isRequired: data.isRequired,
            showOnTicket: data.showOnTicket,
            disabled: data.disabled,
          };
          const res = await createExtraTC(result);
          if ('data' in res) {
            setExtraTCData(res?.data);
            setOpen(false);
            message.success('Successfully created extra T&C.');
          }
        } else {
          const result = {
            id: editExtarTerm.id,
            eventId: eventId,
            htmlMessage: data.htmlMessage,
            message: plainText,
            isRequired: data.isRequired,
            showOnTicket: data.showOnTicket,
            disabled: data.disabled,
          };
          const res = await editExtraTC(result);
          if ('data' in res) {
            setExtraTCData(res?.data);
            setOpen(false);
            message.success('Successfully updated extra T&C.');
          }
        }
      }
    },
    [createExtraTC, plainText, eventId, editExtraTC, editExtarTerm],
  );

  console.log(`PlainText length: ${plainText.length}`, plainText);
  return (
    <div className={s.wrapper}>
      <Link
        to={MY_EVENTS_ROUTE}
        icon="arrowLeft"
        label="My Events"
        labelType="h6"
        className={s.backLink}></Link>
      <div className={s.row}>
        <SectionTitle label="common_extra_tc" className={s.title} />
        <div className={s.titleButton}>
          <Typography type="h4">{eventName}</Typography>

          <Button
            type="primary"
            size="large"
            onClick={showModal}
            style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
            <Icon name="plus" size={20} /> CREATE NEW
          </Button>
        </div>
      </div>
      <div className={s.content}>
        <Label className={s.noMarginLable}>
          This window allows you to create an additional set of terms and conditions that users must
          accept to complete the checkout process. Without agreeing to these terms, they will not be
          able to purchase tickets for the event.
        </Label>
        <div className={s.notificationList}>
          {fetching ? (
            <div className={s.emptyNotificatoins}>
              <Spin indicator={<EventLoadingState size={250} />} />
            </div>
          ) : !extraTCData?.terms || extraTCData?.terms?.length === 0 ? (
            <div className={s.emptyNotificatoins}>
              <div className={s.iconWrapper}>
                <Icon name="bell" color="grey" />
              </div>
              <Typography color="grey">You have no extra T&C for this event yet</Typography>
              <Button
                type="primary"
                size="large"
                onClick={showModal}
                style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
                <Icon name="plus" size={20} /> CREATE NEW
              </Button>
            </div>
          ) : (
            <div className={s.showData}>
              <Row
                gutter={[0, 8]}
                style={{
                  paddingLeft: 24,
                  paddingRight: 24,
                  paddingBottom: 8,
                  borderBottom: '1px solid #eff0f5',
                }}>
                <Col xs={8} sm={16}>
                  <Typography type="label">Extra Term</Typography>
                </Col>
                <Col xs={4} sm={3}>
                  <Typography type="label">Show on ticket</Typography>
                </Col>
                <Col xs={4} sm={2}>
                  <Typography type="label">Required</Typography>
                </Col>
                <Col xs={4} sm={2}>
                  <Typography type="label">Disabled</Typography>
                </Col>
                <Col xs={4} sm={1}>
                  <Typography type="label">Actions</Typography>
                </Col>
              </Row>
              {extraTCData &&
                extraTCData?.terms?.map((editExtarTerm, index) => (
                  <ExtraTCItem
                    key={index}
                    id={editExtarTerm?.id}
                    setEditExtraTC={handleEditItem}
                    handleDeleteItem={handleDeleteItem}
                    extraTC={editExtarTerm}
                  />
                ))}
            </div>
          )}
        </div>
      </div>

      <Modal width={1000} open={open} onOk={handleOk} handleClose={handleCancel} footer={null}>
        <Modal.Header>
          <Typography type="h3" weight={700}>
            {!editExtarTerm ? 'Create' : 'Edit'} extra Terms and Conditions
          </Typography>
        </Modal.Header>
        <Form className={s.form} form={form} onFinish={handleSubmit}>
          <div>
            <Label>Terms (HTML)</Label>
            <Form.Item
              name="htmlMessage"
              rules={[{ required: true, message: 'This field is required.' }]}>
              <ReactQuill value={html} onChange={handleQuillChange} theme="snow" />
            </Form.Item>
          </div>
          <div>
            <Label>Message</Label>
            <div
              style={{
                minHeight: 120,
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '8px 12px',
                backgroundColor: '#eff0f5',
                fontSize: 14,
                lineHeight: 1.5715,
                whiteSpace: 'pre-wrap',
                color: 'rgba(0, 0, 0, 0.85)',
              }}>
              {plainText || ''}
            </div>
          </div>
          <div>
            <div style={{ marginBottom: 16 }}>
              <Typography type="main" weight={700}>
                Show on Ticket?
              </Typography>
              <Form.Item
                name="showOnTicket"
                extra="If chosen, the term and condition will be displayed on the user's tickets."
                initialValue={false} // or false, depending on default
              >
                <Switch />
              </Form.Item>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Typography type="main" weight={700} className={s.noMarginLable}>
                Is Required?
              </Typography>
              <Form.Item
                name="isRequired"
                extra="If required, the user won't be able to check out tickets unless the term is accepted."
                initialValue={true} // or false, depending on default
              >
                <Switch />
              </Form.Item>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Typography type="main" weight={700} className={s.noMarginLable}>
                Disabled?
              </Typography>
              <Form.Item
                name="disabled"
                extra="If set to true the term won't be displayed in the checkout process"
                initialValue={false} // or false, depending on default
              >
                <Switch />
              </Form.Item>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 8,
              gap: 8,
              width: '100%',
            }}>
            <Button
              type="primary"
              size="large"
              loading={creating || editing}
              htmlType="submit"
              style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
              {!editExtarTerm ? 'CREATE' : 'EDIT'}
            </Button>
            <Button
              type="link"
              size="large"
              style={{ fontSize: 13, color: '#7F86A4' }}
              onClick={handleCancel}>
              CANCEL
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ExtraTC;
